import { useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useNavigate } from '../components/nav'
import { History, RefreshCw, AlertCircle, ListChecks, CheckCircle, XCircle, Clock, Timer, ArrowLeft, ChevronLeft, ChevronRight } from 'lucide-react'
import { taskKindLabel, type TaskSummary } from '../data/projectTypes'
import { taskDetailPath } from '../lib/taskDetailPath'
import { useIsAdmin } from '../hooks/useAdminMode'
import { useSignInOnUnauthorized } from '../hooks/useSignInOnUnauthorized'
import BackNavButton from '../components/BackNavButton'
import { useGetProjectQuery, useGetTaskHistoryQuery } from '../services/projectDataApi'
import './ProcessHistory.css'

function formatStartTime(iso: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/** Formats a span of seconds as a compact human-readable duration (e.g. "1m 5s", "2h 3m"). */
function formatDurationSeconds(seconds: number | null): string {
  if (seconds == null) return '—'
  if (seconds < 60) return `${seconds}s`
  const mins = Math.floor(seconds / 60)
  if (mins < 60) return `${mins}m ${seconds % 60}s`
  const hours = Math.floor(mins / 60)
  return `${hours}h ${mins % 60}m`
}

function taskDuration(task: TaskSummary): number | null {
  if (!task.started_at || !task.completed_at) return null
  const secs = Math.round((new Date(task.completed_at).getTime() - new Date(task.started_at).getTime()) / 1000)
  return secs >= 0 ? secs : null
}

const TASK_HISTORY_PAGE_SIZE = 50

function HistoryRow({ task }: { task: TaskSummary }) {
  const navigate = useNavigate()
  const isFailed = task.status === 'failed'
  const detailPath = taskDetailPath(task)
  const version = task.test_run_id && task.journey_version_number != null
    ? `v${task.journey_version_number}`
    : '—'
  return (
    <tr
      className={`process-history__row${detailPath ? ' process-history__row--clickable' : ''}`}
      data-testid={`history-task-${task.id}`}
      data-analytics-event="processing-history-row-open"
      onClick={detailPath ? () => navigate(detailPath) : undefined}
      role={detailPath ? 'link' : undefined}
      style={detailPath ? { cursor: 'pointer' } : undefined}
    >
      <td>{taskKindLabel(task)}</td>
      <td className="process-history__goal">{task.goal || task.journey_name || '—'}</td>
      <td className="process-history__version">{version}</td>
      <td>
        <span className={`process-history__status process-history__status--${isFailed ? 'failed' : 'completed'}`}>
          {isFailed ? <XCircle size={13} aria-hidden="true" /> : <CheckCircle size={13} aria-hidden="true" />}
          {isFailed ? 'Failed' : 'Completed'}
        </span>
      </td>
      <td className="process-history__time">{formatStartTime(task.started_at)}</td>
      <td className="process-history__duration">{formatDurationSeconds(taskDuration(task))}</td>
    </tr>
  )
}

export default function ProcessHistory() {
  const { projectId } = useParams<{ projectId: string }>()
  const navigate = useNavigate()
  const projectQuery = useGetProjectQuery(projectId!, { skip: !projectId })
  const project = projectQuery.data
  // Like the task pages: a signed-out admin following a link here is sent to sign in and back, rather
  // than being told the page is unavailable.
  const redirectingToLogin = useSignInOnUnauthorized(projectQuery.error)
  // Processing history is task-execution detail — admin-only tooling, matching the task-detail page
  // its rows link into. Gate on real admin status rather than the preview toggle, which isn't
  // reachable from this page. The endpoint enforces the same boundary.
  const isAdmin = useIsAdmin()
  const [page, setPage] = useState(1)
  const historyQuery = useGetTaskHistoryQuery({ projectId: projectId!, page }, { skip: !projectId || !isAdmin })
  const history = historyQuery.data
  const historyLoading = historyQuery.isLoading || historyQuery.isFetching
  const historyError = historyQuery.error ? 'Failed to fetch task history' : null
  const refresh = () => { void historyQuery.refetch() }

  if (!isAdmin) {
    // Render nothing for the moment it takes the sign-in redirect to leave the page.
    if (redirectingToLogin) return null
    return (
      <div className="process-history" data-testid="process-history-access-denied">
        <div className="process-history__error">
          <p>This page is not available.</p>
          <Link to={`/projects/${projectId}/overview`} data-analytics-event="processing-history-access-denied-back">
            <ArrowLeft size={16} aria-hidden="true" /> Back to project
          </Link>
        </div>
      </div>
    )
  }

  const stats = history?.stats
  const tasks = history?.tasks ?? []
  // Total spans the whole project; the rows are one page of it.
  const totalPages = Math.max(1, Math.ceil((stats?.total ?? 0) / TASK_HISTORY_PAGE_SIZE))

  return (
    <div className="process-history" data-testid="process-history-page">
      <BackNavButton
        label="Back to Settings"
        onClick={() => navigate(`/projects/${projectId}/settings`)}
        data-testid="history-back-btn"
        data-analytics-event="processing-back"
      />

      <div className="process-history__header">
        <div className="process-history__title-block">
          <h1 className="process-history__title">
            <History size={22} aria-hidden="true" />
            Processing History
          </h1>
          <p className="process-history__subtitle">
            {project?.name ? `Completed and failed runs for ${project.name}.` : 'Completed and failed runs for this project.'}
          </p>
        </div>
        <button
          type="button"
          className="process-history__refresh-btn"
          onClick={refresh}
          data-testid="refresh-history"
          data-analytics-event="processing-history-refresh"
        >
          <RefreshCw size={14} aria-hidden="true" />
          Refresh
        </button>
      </div>

      {historyLoading && !history ? (
        <div className="process-history__loading" data-testid="history-loading">Loading...</div>
      ) : historyError ? (
        <div className="process-history__error" role="alert">
          <AlertCircle size={20} aria-hidden="true" />
          <p>{historyError}</p>
          <button type="button" className="process-history__retry-btn" onClick={refresh} data-analytics-event="processing-history-retry">Retry</button>
        </div>
      ) : (
        <>
          <section className="process-history__stats" data-testid="history-stats">
            <div className="process-history__stat-card">
              <div className="process-history__stat-icon"><ListChecks size={18} aria-hidden="true" /></div>
              <div className="process-history__stat-body">
                <span className="process-history__stat-value" data-testid="stat-total">{stats?.total ?? 0}</span>
                <span className="process-history__stat-label">Total tasks run</span>
              </div>
            </div>
            <div className="process-history__stat-card">
              <div className="process-history__stat-icon process-history__stat-icon--completed"><CheckCircle size={18} aria-hidden="true" /></div>
              <div className="process-history__stat-body">
                <span className="process-history__stat-value" data-testid="stat-completed">{stats?.completed ?? 0}</span>
                <span className="process-history__stat-label">Completed</span>
              </div>
            </div>
            <div className="process-history__stat-card">
              <div className="process-history__stat-icon process-history__stat-icon--failed"><XCircle size={18} aria-hidden="true" /></div>
              <div className="process-history__stat-body">
                <span className="process-history__stat-value" data-testid="stat-failed">{stats?.failed ?? 0}</span>
                <span className="process-history__stat-label">Failed</span>
              </div>
            </div>
            <div className="process-history__stat-card">
              <div className="process-history__stat-icon"><Clock size={18} aria-hidden="true" /></div>
              <div className="process-history__stat-body">
                <span className="process-history__stat-value" data-testid="stat-total-duration">{formatDurationSeconds(stats?.total_duration_seconds ?? 0)}</span>
                <span className="process-history__stat-label">Total run time</span>
              </div>
            </div>
            <div className="process-history__stat-card">
              <div className="process-history__stat-icon"><Timer size={18} aria-hidden="true" /></div>
              <div className="process-history__stat-body">
                <span className="process-history__stat-value" data-testid="stat-avg-duration">{formatDurationSeconds(stats?.avg_duration_seconds ?? 0)}</span>
                <span className="process-history__stat-label">Average duration</span>
              </div>
            </div>
          </section>

          <section className="process-history__section">
            <h2 className="process-history__section-title">Task Runs</h2>
            {tasks.length === 0 ? (
              <p className="process-history__empty" data-testid="history-empty">
                No tasks have run for this project yet.
              </p>
            ) : (
              <div className="process-history__table-wrap" data-testid="history-table">
                <table className="process-history__table">
                  <thead>
                    <tr>
                      <th>Type</th>
                      <th>Goal</th>
                      <th>Version</th>
                      <th>Status</th>
                      <th>Started</th>
                      <th>Duration</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tasks.map((task) => (
                      <HistoryRow key={task.id} task={task} />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {totalPages > 1 && (
              <div className="process-history__pager" data-testid="history-pager">
                <button
                  type="button"
                  className="process-history__pager-btn"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1 || historyLoading}
                  data-testid="history-prev-page"
                  data-analytics-event="processing-history-prev-page"
                >
                  <ChevronLeft size={14} aria-hidden="true" /> Previous
                </button>
                <span className="process-history__pager-label" data-testid="history-page-label">
                  Page {page} of {totalPages}
                </span>
                <button
                  type="button"
                  className="process-history__pager-btn"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages || historyLoading}
                  data-testid="history-next-page"
                  data-analytics-event="processing-history-next-page"
                >
                  Next <ChevronRight size={14} aria-hidden="true" />
                </button>
              </div>
            )}
          </section>
        </>
      )}
    </div>
  )
}
